import {StampSceneView} from "../stamp/stamps.tsx";
import {
    allTransfersForBundleTqo,
    ensureAllBundlesForWaste,
    ensureAllWasteRevisions,
    ensureFullAllTransfersForBundle,
    ensureStampsForWaste,
    ensureWasteById,
    KingBasura,
    SortOrder,
    Transfer,
    useAllBundlesForWaste,
    useAllWasteRevisions,
    useBundleForWasteAsof,
    useLastTransferForBundleBefore,
    useStampsForWaste,
    useWasteById,
    Waste,
    WasteStamp,
    WasteStampFlavor,
    watchTransferByIdTqo,
} from "../rpc/webrpc-client.gen.ts";
import {Alert, Box, SheetProps, Stack} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {TruckCapsule} from "../truck/capsule.tsx";
import {BaseTimelineScene, cmpScenesNewestFirst} from "../timeline/timeline.tsx";
import {useEffect, useMemo} from "react";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {z} from "zod/v4";
import {synthTransferScenes, TransferScene} from "../transit/transfer.tsx";
import {QueryClient, queryOptions, skipToken, useQueries, useQueryClient} from "@tanstack/react-query";

import {BundlePOVTransferScene, BundleTransferSceneView} from "../bundle/timeline.tsx";
import {spawnTstzSpan} from "../tstzspan.ts";
import {AgentCapsule} from "../agent/capsule.tsx";
import {faPencil} from "@fortawesome/pro-regular-svg-icons";
import {StampFlavorIconMap} from "../stamp/stampFlavorIconMap.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {DiarySceneView, DiaryView} from "../timeline/diary.tsx";
import {InfallibleUseQueryResult} from "../util/useInfallibleQuery.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {mergeUseQueriesWithInfill} from "../tq/mergeUseQueriesWithInfill.tsx";
import {cleanse, NADA} from "../util/arutil.tsx";
import {useMothership} from "../tq/tqx.tsx";

const WasteSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("waste"),
    subject: Waste,
});

export const WasteStampScene = z.object({
    ...WasteSceneBase.shape,
    sceneFlavor: z.literal("stamp"),
    stamp: WasteStamp,
});
export type WasteStampScene = z.infer<typeof WasteStampScene>;

export const WastePOVTransferScene = z.object({
    ...WasteSceneBase.shape,
    sceneFlavor: z.literal("transfer"),
    transferScene: TransferScene,
});
export type WastePOVTransferScene = z.infer<typeof WastePOVTransferScene>;

function rebadgeTransferScene(from: TransferScene, waste: Waste): WastePOVTransferScene {
    const rv: WastePOVTransferScene = {
        subjectFlavor: "waste",
        subject: waste,
        sceneFlavor: "transfer",
        transferScene: from,
        when: from.when,
        where: from.where,
    };
    return rv;
}

export const WasteEditScene = z.object({
    ...WasteSceneBase.shape,
    sceneFlavor: z.literal("edit"),
    prev: Waste,
    next: Waste,
});
export type WasteEditScene = z.infer<typeof WasteEditScene>;

export const WasteScene
    = z.discriminatedUnion("sceneFlavor", [
    WasteStampScene,
    WastePOVTransferScene,
    WasteEditScene,
]);
export type WasteScene = z.infer<typeof WasteScene>;

function synthWasteStampScenes({waste, stamps}: { waste: Waste, stamps: WasteStamp[] }): WasteStampScene[] {
    const rv = stamps.map(stamp => ({
        subjectFlavor: "waste",
        subject: waste,
        sceneFlavor: "stamp",
        stamp,
        when: {at: stamp.posted}
    } as const));

    return rv;
}

function synthWastePOVTransferScenes({waste, transfers}: {
    waste: Waste,
    transfers: Transfer[]
}): WastePOVTransferScene[] {
    const rv = synthTransferScenes({transfers})
        .map(xferScene => rebadgeTransferScene(xferScene, waste));
    return rv;
}

function synthWasteEditScenes({waste, revisions}: { waste: Waste, revisions: Waste[] }): WasteEditScene[] {
    const rv: WasteEditScene[] = [];
    const nrevs = revisions.length;
    for (let i = 0; i < nrevs - 1; i++) {
        const prev = revisions[i];
        const next = revisions[i + 1];
        if (prev && next) {
            const nextValidSpan = spawnTstzSpan(next.validSpan);
            const changeover = nextValidSpan.lower;
            if (changeover) {
                const scene: WasteEditScene = {
                    subjectFlavor: "waste",
                    subject: waste,
                    sceneFlavor: "edit",
                    prev, next, when: {at: changeover},
                }
                rv.push(scene);
            }
        }
    }
    return rv;
}

const WasteTimelineFood = z.object({
    waste: Waste,
    stamps: WasteStamp.array(),
    transfers: Transfer.array(),
    revisions: Waste.array(),
});

type WasteTimelineFood = z.infer<typeof WasteTimelineFood>;

function synthWasteScenes(food: WasteTimelineFood): WasteScene[] {
    const unsorted = [
        ...synthWasteStampScenes(food),
        ...synthWastePOVTransferScenes(food),
        ...synthWasteEditScenes(food),
    ];

    const sorted = unsorted.toSorted(cmpScenesNewestFirst);
    return sorted;
}

function BundleOnloadedCapsule({bundleId, asof}: { bundleId: string, asof: string }) {
    const {t} = useTranslation();
    const lastTransferTqrv = useLastTransferForBundleBefore({bundleId, before: asof});
    const xfer = lastTransferTqrv.data;
    if (lastTransferTqrv.isPending) {
        return null;
    }

    if (!xfer) {
        return <Alert color="danger">
            {t("noTransferForBundleAsof", {bundleId, asof})}
        </Alert>
    } else {
        return <Stack direction="row" spacing={2} sx={{
            alignItems: "center", justifyContent: "stretch",
            fontSize: "lg"
        }}>
            <Box>{t("onloaded")}</Box>
            <TruckCapsule sx={{fontSize: "md"}} truck={xfer.truck}/>
        </Stack>
    }
}

function WasteOnloadedStampCapsule({stamp}: { stamp: WasteStamp }) {
    const {t} = useTranslation();
    const {data: bundle, isSuccess: isBundleSuccess}
        = useBundleForWasteAsof({wasteId: stamp.subjectId, asof: stamp.posted});
    if (!isBundleSuccess) {
        return null;
    }
    if (!bundle) {
        return <Alert color="danger">
            {t("noBundleForWasteAsof", {wasteId: stamp.subjectId, asof: stamp.posted})}
        </Alert>
    } else {
        return <BundleOnloadedCapsule bundleId={bundle.id} asof={stamp.posted}/>
    }
}

export const iconsByWasteStampFlavor: StampFlavorIconMap<"waste"> = {} as const;

const diaryColorsForWasteStampFlavors: Partial<Record<WasteStampFlavor, SheetProps["color"]>> = {
    ONLOADED: "success",
    PRELIM_INSPECT: "warning",
    FINAL_INSPECT: "success",
};

function WasteStampSceneView({scene, ...rest}: SceneViewProps<WasteStampScene>) {
    const stamp = scene.stamp;
    const stampFlavor = stamp.flavor;
    const color = stampFlavor
        ? diaryColorsForWasteStampFlavors[stampFlavor]
        : undefined;
    return <StampSceneView scene={scene} iconMap={iconsByWasteStampFlavor} color={color} {...rest}>
        {stampFlavor === WasteStampFlavor.enum.ONLOADED
            ? <WasteOnloadedStampCapsule stamp={stamp}/>
            : null}
    </StampSceneView>
}

function WasteTransferSceneView({scene, ...rest}: SceneViewProps<WastePOVTransferScene>) {
    const transferScene = scene.transferScene;
    const transfer = transferScene.subject;
    const bundle = transfer.bundle;

    const bundleScene = useMemo<BundlePOVTransferScene>(() => ({
        subjectFlavor: "bundle",
        subject: bundle,
        sceneFlavor: "transfer",
        transferScene,
        when: transferScene.when,
    }), [bundle, transferScene]);

    return <BundleTransferSceneView scene={bundleScene} {...rest} />
}

function WasteEditSceneView({scene, ...rest}: SceneViewProps<WasteEditScene>) {
    const {t} = useTranslation();
    return <DiarySceneView scene={scene} {...rest}>
        <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
            <AgentCapsule agent={{...scene.next.author, icon: {icon: faPencil}}}/>
            <Box>{t("edit")}</Box>
        </Stack>
    </DiarySceneView>
}

function WasteSceneView({scene, ...rest}: SceneViewProps<WasteScene>) {
    switch (scene.sceneFlavor) {
        case "stamp":
            return <WasteStampSceneView scene={scene} {...rest}/>;
        case "transfer":
            return <WasteTransferSceneView scene={scene} {...rest}/>;
        case "edit":
            return <WasteEditSceneView scene={scene} {...rest} />;
    }
}

export interface WatchWasteTimelineArgs {
    wasteId: string;
}

export const wasteTimelineVerb = "WasteTimeline"

export function wasteTimelineTqk(args ?: WatchWasteTimelineArgs) {
    return args ? [wasteTimelineVerb, args] as const : [wasteTimelineVerb] as const;
}

// this can go stale unless there is a relay mounted watching over it

export function ensureUnsortedWasteTimeline({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args: WatchWasteTimelineArgs,
}): Promise<WasteScene[]> {
    const qa = {tqc, mama, args} as const;
    return Promise.all([
        ensureWasteById(qa),
        ensureStampsForWaste(qa),
        ensureAllBundlesForWaste(qa),
        ensureAllWasteRevisions(qa),
    ]).then(([{waste}, {stamps}, {bundles: bundleStubs}, {revisions}]) => {
        return Promise.all(bundleStubs.map(stub =>
            ensureFullAllTransfersForBundle({tqc, mama, args: {bundleId: stub.id}})
                .then(rpcs => rpcs.map(rpc => rpc.transfer))))
            .then(transferSets => {
                const uniq = new Map<string, Transfer>();
                for (const xset of transferSets) {
                    for (const xfer of xset) {
                        uniq.set(xfer.id, xfer);
                    }
                }
                const transfers = uniq.values();
                const rv = {waste, stamps, transfers: Array.from(transfers), revisions} as const;
                return rv;
            });
    }).then((got) => {
        const food = WasteTimelineFood.parse(got);
        return food;
    }).then(food => {
        const scenes = synthWasteScenes(food);
        return scenes;
    });
}

export function wasteTimelineTqo({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args?: WatchWasteTimelineArgs
}) {
    return queryOptions({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: wasteTimelineTqk(args),
        queryFn: args
            ? () => ensureUnsortedWasteTimeline({tqc, mama, args})
            : skipToken,
    });
}

export function useWasteTimeline(args?: WatchWasteTimelineArgs): InfallibleUseQueryResult<WasteScene[], Error> {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useRefreshableInfallibleQuery(wasteTimelineTqo({tqc, mama, args}));
}

export const lastSceneForWasteVerb = "LastSceneForWaste";

export function lastSceneForWasteTqk(args ?: WatchWasteTimelineArgs) {
    return args ? [lastSceneForWasteVerb, args] as const : [lastSceneForWasteVerb] as const;
}

export function ensureLastSceneForWaste(
    args: {
        tqc: QueryClient, mama: KingBasura, args: WatchWasteTimelineArgs
    }
) {
    return ensureUnsortedWasteTimeline(args)
        .then(unsorted => unsorted.toSorted(cmpScenesNewestFirst))
        .then(sorted => sorted[0] ?? null);
}

export function lastSceneForWasteTqo(mama: KingBasura, args?: WatchWasteTimelineArgs) {
    return queryOptions({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: lastSceneForWasteTqk(args),
        queryFn: args
            ? ({client: tqc}) => ensureLastSceneForWaste({tqc, mama, args})
            : skipToken,
    });
}

export function useLastSceneForWaste(args?: WatchWasteTimelineArgs) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery(lastSceneForWasteTqo(mama, args));
}

export function WasteTimelineRelay(args: { wasteId: string }) {
    const tqc = useQueryClient();
    const mama = useMothership();
    const wasteId = args.wasteId;
    const tqk = wasteTimelineTqk(args);

    const wasteTqrv = useWasteById(args);
    const stampTqrv = useStampsForWaste(args);
    const bundlesTqrv = useAllBundlesForWaste(args);
    const xfersTqos = (bundlesTqrv.data ?? []).map(bundleStub =>
        allTransfersForBundleTqo(mama, {bundleId: bundleStub.id}));
    const xfersTqrv = useQueries(bundlesTqrv.data
        ? {
            queries: xfersTqos,
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const uniqueTransferIds = useMemo(() => {
        const uniq = new Set<string>();
        if (bundlesTqrv.data) {
            for (const xset of xfersTqrv.data) {
                if (xset) {
                    for (const xfer of xset) {
                        uniq.add(xfer.id)
                    }
                }
            }
        }
        return Array.from(uniq.values());
    }, [bundlesTqrv.data, xfersTqrv.data]);

    const transfersTqrv = useQueries(xfersTqrv.isSuccess
        ? {
            queries: uniqueTransferIds.map(transferId => watchTransferByIdTqo(mama, {transferId})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    const revisionsTqrv = useAllWasteRevisions(args);

    const synthFoodTqrv = useMemo(() => {
        const maybe: Partial<WasteTimelineFood> = {
            waste: wasteTqrv.data,
            stamps: stampTqrv.data,
            transfers: transfersTqrv.isSuccess ? cleanse(transfersTqrv.data) : undefined,
            revisions: revisionsTqrv.data,
        };
        const zprv = WasteTimelineFood.safeParse(maybe);
        return zprv;
    }, [revisionsTqrv.data, stampTqrv.data, transfersTqrv.data, transfersTqrv.isSuccess, wasteTqrv.data]);

    useEffect(() => {
        if (synthFoodTqrv.success) {
            const scenes = synthWasteScenes(synthFoodTqrv.data);
            tqc.setQueryData(tqk, scenes);
            const latestScene = scenes[0];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (latestScene) {
                tqc.setQueryData(lastSceneForWasteTqk({wasteId}), latestScene);
            }
        }
    }, [synthFoodTqrv.data, synthFoodTqrv.success, tqc, tqk, wasteId]);

    return null;
}

export interface WasteDiaryViewProps {
    wasteId: string;
    sortOrder: SortOrder;
//    onFlavorChange?: (newFlavor: WasteFlavorStub) => void;
}

export function WasteDiaryView({wasteId, sortOrder}: WasteDiaryViewProps) {
    // const {data: waste, isPending: isWastePending} = useWasteById({wasteId});
    // useEffect(() => {
    //     if (waste?.flavor && onFlavorChange) {
    //         onFlavorChange(waste.flavor);
    //     }
    // }, [onFlavorChange, waste?.flavor]);

    const timelineTqrv = useWasteTimeline({wasteId});
    const rawScenes = timelineTqrv.data;
    const sortedScenes = useMemo(() => {
        switch (sortOrder) {
            case "ASC":
                return rawScenes?.toReversed() ?? [];
            case "DESC":
                return rawScenes ?? [];
        }
    }, [rawScenes, sortOrder]);

    if (/*isWastePending ||*/ timelineTqrv.isPending) {
        return <SuspenseDialog message="fetching scenes"/>
    }

    return <DiaryView<WasteScene> sortedScenes={sortedScenes}
                                  Entry={WasteSceneView}/>
}

